"use client";

import type { CSSProperties } from "react";
import { SHEET_SHADOW } from "@/components/ui/design-system/sheet";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { SURFACE } from "@/components/ui/design-system/surface-styles";
import { Display } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { Card } from "../one/ui";
import { useRestingTop } from "./useRestingTop";
import { useStuckAtBottom } from "./useStuckAtBottom";

/**
 * The detail page's column: the feed's, so the title lands where the feed's
 * title was. Owned here so the shell skeleton and the page share it without
 * the shell importing the page.
 */
export const REQUEST_DETAIL_COLUMN =
  "mx-auto flex w-full max-w-[1120px] flex-1 flex-col gap-nf-ds-6 pt-nf-ds-4 lg:py-nf-ds-6";

/** A caption bone over a run of list lines: the brief's SHOOT and EVERY VIDEO. */
function Lines({ count, caption }: { count: number; caption: boolean }) {
  return (
    <div className="flex flex-col gap-nf-ds-2">
      {caption ? <Skeleton className="h-[10px] w-[48px]" /> : null}
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
          key={i}
          className="h-[14px]"
          style={{ width: `${[92, 78, 86, 64][i % 4]}%` }}
        />
      ))}
    </div>
  );
}

/**
 * The detail page before its request arrives, on the real surfaces in the
 * real grid: the static "Request" title with a bone where the name goes;
 * the brief pane with the brief's own structure (name, one line, the shoot
 * steps, the script, the rules, then the checklist); and the aside with the
 * Examples heading, a 2x2 of frames, and the money card and button pinned
 * to the bottom of the viewport as the live aside pins them. On a phone the
 * frames are a clipped row at the shelf's tile width.
 *
 * MIRRORS `CreatorRequestDetail`, `RfcCreatorBrief`, `SpecChecklist` and
 * `RequestReferenceStrip`. Change a container there and this is the file
 * that has to move with it.
 */
export function RequestDetailSkeleton() {
  const t = useCreatorTranslations("requests.detail");
  const { ref: asideRef, top: asideTop } = useRestingTop<HTMLElement>();
  const offer = useStuckAtBottom();
  return (
    <div className={REQUEST_DETAIL_COLUMN} aria-busy="true">
      <span className="sr-only">{t("loading")}</span>
      {/* Drawn by hand rather than through `PageHeader`: its subtitle is a
          `<p>`, and the Skeleton is a `<div>`, which cannot sit inside one.
          Same `Display` role and the header's own spacing. */}
      <div className="space-y-1">
        <Display>{t("title")}</Display>
        <Skeleton className="h-[20px] w-[320px] max-w-full" />
      </div>
      <div
        aria-hidden
        className="flex flex-col gap-nf-ds-6 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-nf-ds-8"
      >
        {/* Pinned the way the live aside is: at its own measured resting
            position, so the money card lands where it will stay once the
            page swaps in, whatever the header's height. */}
        <aside
          ref={asideRef}
          className="flex min-w-0 flex-col gap-nf-ds-6 lg:sticky lg:top-[var(--aside-top)] lg:order-2 lg:h-[calc(100dvh-var(--aside-top)-var(--offer-h)-var(--spacing-nf-ds-6)-var(--spacing-nf-ds-8))] lg:self-start"
          style={
            {
              "--aside-top": `${asideTop ?? 0}px`,
              "--offer-h": `${offer.height}px`,
            } as CSSProperties
          }
        >
          <div className="flex flex-col gap-nf-ds-4 lg:min-h-0 lg:flex-1 lg:overflow-hidden">
            <div className="flex flex-col gap-nf-ds-2">
              <Skeleton className="h-[18px] w-[96px]" />
              <Skeleton className="h-[14px] w-[280px] max-w-full" />
            </div>
            <div className="flex gap-nf-ds-4 overflow-hidden lg:grid lg:grid-cols-2">
              {["a", "b", "c", "d"].map((key, index) => (
                <div
                  key={key}
                  className={
                    index < 2
                      ? "flex w-[240px] shrink-0 flex-col gap-nf-ds-2 lg:w-auto"
                      : "hidden flex-col gap-nf-ds-2 lg:flex"
                  }
                >
                  <Skeleton className="aspect-[2/3] w-full rounded-nf-ds-card" />
                  <div className="flex h-nf-ds-6 items-center justify-center">
                    <Skeleton className="h-[12px] w-[64px]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
        <div className="flex min-w-0 flex-col gap-nf-ds-6 lg:order-1">
          <Card className="flex flex-col gap-nf-ds-5">
            <Skeleton className="h-[16px] w-[72px]" />
            <div className="flex flex-col gap-nf-ds-2">
              <Skeleton className="h-[16px] w-[160px]" />
              <Skeleton className="h-[14px] w-[88%]" />
            </div>
            <Lines count={3} caption />
            <Lines count={4} caption />
            <Skeleton className="h-[14px] w-[60%]" />
            <Lines count={4} caption />
            {/* The checklist folded to its one row: the heading, the count
                and the chevron. */}
            <div className="mt-nf-ds-1 flex items-center gap-nf-ds-3 py-nf-ds-3">
              <Skeleton className="h-nf-ds-4 w-nf-ds-32" />
              <Skeleton className="ml-auto h-nf-ds-4 w-nf-ds-12" />
              <Skeleton className="size-nf-ds-4 rounded-full" />
            </div>
          </Card>
        </div>
      </div>
      <div
        ref={offer.ref}
        className={cn(
          SURFACE,
          "sticky bottom-[calc(var(--one-tabbar)+var(--one-inset))] z-10 -mx-nf-ds-6 flex flex-col gap-nf-ds-4 px-nf-ds-6 pt-nf-ds-5 pb-nf-ds-4",
          "max-lg:rounded-b-none lg:mx-0 lg:bottom-nf-ds-6 lg:flex-row lg:items-center lg:gap-nf-ds-8 lg:px-nf-ds-8 lg:py-nf-ds-5",
          SHEET_SHADOW,
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-nf-ds-2">
          <div className="flex items-center gap-nf-ds-2">
            <Skeleton className="size-5 rounded-full" />
            <Skeleton className="h-[13px] w-[160px] max-w-full" />
          </div>
          <div className="flex items-baseline gap-nf-ds-6">
            <div className="flex items-baseline gap-nf-ds-2">
              <Skeleton className="h-[30px] w-[88px]" />
              <Skeleton className="h-[14px] w-[72px]" />
            </div>
            <div className="flex items-baseline gap-nf-ds-2">
              <Skeleton className="h-[30px] w-[72px]" />
              <Skeleton className="h-[14px] w-[28px]" />
            </div>
          </div>
          <Lines count={3} caption={false} />
        </div>
        <Skeleton className="h-[48px] w-full rounded-nf-ds-pill lg:w-[280px]" />
      </div>
      <div ref={offer.sentinelRef} aria-hidden className="-mt-nf-ds-6 h-0" />
    </div>
  );
}
